/** Author: Claude Code // Generation: Animation Engine integration // Version: v3.2.225 // Modified: 2025-11-14 */
import { GameEvent } from './types';
import { ISceneAdapter } from './adapters/SceneAdapter';
import { ICombatAdapter } from './adapters/CombatAdapter';
import { EventTypes } from '../../../../../backend/src/core/events/GameEventTypes';
import { normalizeColor } from '../../../utils/colorMapping';

export class SinglePathAnimationEngine {
  private sceneAdapter: ISceneAdapter | null = null;
  private combatAdapter: ICombatAdapter | null = null;
  private ready = { scene: false, theater: false };
  private queues: Map<string, GameEvent[]> = new Map();
  private currentPriority: Map<string, number> = new Map();
  // 移除全局死亡锁；死亡播放顺序通过剧场与移动完成闸门保证
  private stepInterval: number = 500; // 队列步骤间隔，0.5秒
  // 去重：记录每个agent最后处理的后端序列号（由桥接层注入到 data.__seq）
  private lastSeq: Map<string, number> = new Map();
  // 移动状态：在walk期间标记，收集事件需要等待
  private movingAgents: Set<string> = new Set();
  // 战斗序列号追踪：记录每个agent最近参与的战斗事件seq，用于死亡时序判断 (v3.2.8)
  private recentCombatSeq: Map<string, number> = new Map();

  setAdapters(scene: ISceneAdapter, combat: ICombatAdapter) {
    this.sceneAdapter = scene;
    this.combatAdapter = combat;
  }

  setReady(flags: { scene?: boolean; theater?: boolean }) {
    if (typeof flags.scene === 'boolean') this.ready.scene = flags.scene;
    if (typeof flags.theater === 'boolean') this.ready.theater = flags.theater;
  }

  setStepInterval(interval: number) {
    this.stepInterval = Math.max(100, interval); // 最小100ms
    console.log(`🎬 Engine: 设置队列步骤间隔为 ${this.stepInterval}ms`);
  }

  getStepInterval(): number {
    return this.stepInterval;
  }

  handle(event: GameEvent) {
    const { type, agentId, data = {} } = event;

    console.log(`🎬 SinglePathAnimationEngine: Received event ${type} for agent ${agentId}`);

    // 🔍 详细记录战斗相关事件
    if (type.includes('COMBAT') || type.includes('combat')) {
      console.log(`📥 前端接收: ${type} - agentId:${agentId}, data:`, data);
    }

    // 移除 deathLock 行为：不再全局忽略事件，由上层状态决定是否派发

    // readiness gate
    if (!this.isPathReady(type)) {
      console.log(`⏳ SinglePathAnimationEngine: Path not ready for ${type}, enqueueing`);
      this.enqueue(agentId, event);
      return;
    }

    // 针对淘汰事件：严格排队到队尾，不允许抢占，确保其为该agent的最后可视化事件
    if (type === EventTypes.AGENT_ELIMINATED) {
      this.enqueue(agentId, event); // 入队，稍后按FIFO处理
      return;
    }

    // 去重/排序：如携带后端序列号，仅用于丢弃已处理过的旧事件
    const seq = typeof (data as any).__seq === 'number' ? (data as any).__seq as number : undefined;
    if (seq !== undefined) {
      const last = this.lastSeq.get(agentId) ?? -1;
      if (seq <= last) {
        console.log(`↩️ Engine: drop duplicate/old event ${type} seq=${seq} (last=${last}) for ${agentId}`);
        return;
      }
      // 注意：不在这里更新 lastSeq，改为事件真正处理完成后更新
    }

    // 不在Engine层对战斗事件做全局节流，保持简单的优先级调度

    // priority gate: equal or lower does not interrupt
    const newPrio = this.priorityOf(type);
    const curPrio = this.currentPriority.get(agentId) ?? Number.MAX_SAFE_INTEGER;
    if (newPrio >= curPrio && curPrio !== Number.MAX_SAFE_INTEGER) {
      console.log(`🔄 SinglePathAnimationEngine: Priority ${newPrio} not higher than current ${curPrio}, enqueueing ${type}`);
      this.enqueue(agentId, event);
      return;
    }

    console.log(`✅ SinglePathAnimationEngine: Processing ${type} with priority ${newPrio} for agent ${agentId}`);
    this.currentPriority.set(agentId, newPrio);

    // Scene layer
    if (type === EventTypes.AGENT_SPAWNED) {
      const position = data.position || data.agent?.position || { x: 0, y: 0 };
      const rawColor = data.color || data.agent?.color || 'green';
      const color = normalizeColor(String(rawColor)); // 最终保底：标准化颜色名
      const initialHealth = data.agent?.health || data.health; // 开场HP用于地图血条初始化
      console.log(`🎭 SinglePathAnimationEngine: Scene route → SceneAdapter.idle(${agentId}) with spawn at (${position.x}, ${position.y}) color: ${color}`);
      this.sceneAdapter?.idle(agentId, {
        spawn: true,
        gridX: position.x,
        gridY: position.y,
        color, // 🔧 传递颜色信息
        initialHealth
      });
      this.finish(agentId, seq);
      return;
    }
    if (type === EventTypes.AGENT_MOVED) {
      // Fix: Support multiple position data formats (position, toPosition, to)
      const targetPos = data.position || data.toPosition || data.to || { x: 0, y: 0 };
      console.log(`🚶 SinglePathAnimationEngine: Scene route → SceneAdapter.walk(${agentId}) to (${targetPos.x}, ${targetPos.y})`);

      // 🔧 传递移动完成回调，确保移动动画完成后才处理下一个事件
      this.movingAgents.add(agentId);
      this.sceneAdapter?.walk(agentId, targetPos, () => {
        this.movingAgents.delete(agentId);
        const hasMoreMoves = this.queues.get(agentId)?.some(ev => ev.type === EventTypes.AGENT_MOVED) || false;
        console.log(`✅ Engine: ${agentId} movement complete, hasMoreMoves=${hasMoreMoves}`);

        // 🎯 状态机关键：仅当队列中没有其他移动事件时，才发送"所有移动完成"ACK
        if (!hasMoreMoves) {
          this.notifyAllMovementsComplete(agentId);
        }

        // 🛰️ 广播移动完成（供回放等待真实结束）
        try {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('ABR_MOVE_DONE', { detail: { agentId, timestamp: Date.now(), pathDone: true } }));
          }
        } catch {}

        this.finish(agentId, seq, true); // 🔥 移动完成立即处理队列，不延迟
      });
      return;
    }
    if (type === EventTypes.AGENT_ELIMINATED) {
      // 🎯 v3.2.8: 检查死亡事件是否紧随战斗事件，若是则判断是否需要延迟等待剧场
      if (seq !== undefined) {
        const lastCombatSeq = this.recentCombatSeq.get(agentId) || -1;

        // 如果死亡seq与最近战斗seq相距不超过10，可能需要等待剧场
        if (lastCombatSeq > 0 && seq - lastCombatSeq < 10) {
          console.log(`[Engine-Death-Delay] ${agentId} death seq=${seq} close to combat seq=${lastCombatSeq}, checking...`);

          // 检查agent是否还在活跃战斗中（剧场未关闭）
          const isInCombat = this.combatAdapter && typeof (this.combatAdapter as any).isAgentInActiveCombat === 'function'
            ? (this.combatAdapter as any).isAgentInActiveCombat(agentId)
            : false;

          if (isInCombat) {
            console.log(`[Engine-Death-Delay] ⏳ Delaying death for ${agentId}, combat theater still active`);
            setTimeout(() => this.handle(event), 500);
            return;
          }
        }
      }

      // 如仍有移动在进行或排队，等待移动完成后再播放死亡
      const hasQueuedMove = (this.queues.get(agentId)?.some(ev => ev.type === EventTypes.AGENT_MOVED)) || false;
      if (this.movingAgents.has(agentId) || hasQueuedMove) {
        console.log(`[Engine-Death-Delay] ⏳ Delaying death for ${agentId} until movement completes`);
        setTimeout(() => this.handle(event), Math.max(100, this.stepInterval));
        return;
      }

      // 正常执行死亡
      console.log(`💀 SinglePathAnimationEngine: Scene route → SceneAdapter.die(${agentId})`);
      this.sceneAdapter?.die(agentId);
      // 🛰️ 广播淘汰完成（非剧场路径）
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('ABR_ELIMINATION_DONE', { detail: { agentId, timestamp: Date.now() } }));
        }
      } catch {}
      this.finish(agentId, seq);
      return;
    }
    if (type === EventTypes.RESOURCE_COLLECTED) {
      const t = (data.type || data.resourceType || data.item?.category || '').toString().toUpperCase();
      const itemId = data.itemId || data.resourceId || data.item?.itemId;
      // 若还在移动期间或队列中尚有移动，整体延后到移动完成后再执行“连带”清理
      const hasQueuedMove = (this.queues.get(agentId)?.some(ev => ev.type === EventTypes.AGENT_MOVED)) || false;
      if (this.movingAgents.has(agentId) || hasQueuedMove) {
        console.log(`⏳ Engine: defer RESOURCE_COLLECTED (and visual removal) until move finishes for ${agentId}`);
        setTimeout(() => this.enqueue(agentId, event), Math.max(50, this.stepInterval / 2));
        return;
      }
      // 执行“连带”动作：从地图上移除资源 sprite（无论武器/医疗）
      if (itemId && typeof (this.sceneAdapter as any)?.removeResourceSprite === 'function') {
        try { (this.sceneAdapter as any).removeResourceSprite(itemId); } catch {}
      }
      if (t === 'WEAPON') {
        // 武器的角色动画交给 WEAPON_EQUIPPED（已单独排队），此处不播放角色动画
        console.log(`⚔️ SinglePathAnimationEngine: RESOURCE_COLLECTED(WEAPON) visual removal done, waiting for WEAPON_EQUIPPED`);
      } else {
        console.log(`💊 SinglePathAnimationEngine: RESOURCE_COLLECTED(MEDICAL) visual removal done`);
      }
      // 🛰️ 广播资源收集完成
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('ABR_RESOURCE_COLLECT_DONE', { detail: { agentId, itemId, timestamp: Date.now() } }));
        }
      } catch {}
      
      this.finish(agentId, seq);
      return;
    }
    if (type === EventTypes.WEAPON_EQUIPPED) {
      // 若仍有移动未完成，或队列中还有移动事件，延后处理装备，确保先到达最终坐标
      const hasQueuedMove = (this.queues.get(agentId)?.some(ev => ev.type === EventTypes.AGENT_MOVED)) || false;
      if (this.movingAgents.has(agentId) || hasQueuedMove) {
        this.enqueue(agentId, event);
        return;
      }
      const weaponType = (data.weaponType || data.weapon?.weaponType || data.weapon?.type || 'SWORD') as string;
      console.log(`🛡️ SinglePathAnimationEngine: Equip → setEquippedWeapon(${weaponType}) then atk for ${agentId}`);
      try { (this.sceneAdapter as any)?.setEquippedWeapon?.(agentId, weaponType); } catch {}
      this.sceneAdapter?.onComplete(agentId, () => {
        // 🛰️ 广播武器装备完成
        try {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('ABR_WEAPON_EQUIP_DONE', { detail: { agentId, weaponType, timestamp: Date.now() } }));
          }
        } catch {}
        this.finish(agentId, seq);
      });
      this.sceneAdapter?.atk(agentId);
      return;
    }
    if (type === EventTypes.ITEM_USED) {
      // 若移动未完成或仍有移动事件排队，延后使用
      const hasQueuedMove = (this.queues.get(agentId)?.some(ev => ev.type === EventTypes.AGENT_MOVED)) || false;
      if (this.movingAgents.has(agentId) || hasQueuedMove) {
        this.enqueue(agentId, event);
        return;
      }
      const itemType = data.itemType || data.item?.name || 'LARGE_MEDKIT';
      console.log(`🧪 SinglePathAnimationEngine: Scene route → SceneAdapter.use(${agentId}) with item ${itemType}`);
      this.sceneAdapter?.onComplete(agentId, () => this.finish(agentId, seq));
      this.sceneAdapter?.use(agentId, { itemType });
      return;
    }

    // 🔥 SafeZone事件处理
    if (type === EventTypes.SAFEZONE_WARNING) {
      const safeZone = data.safeZone || {};
      const center = safeZone.center || { x: 0, y: 0 };
      const fromRadius = safeZone.currentRadius || 100;
      const toRadius = safeZone.nextRadius || 50;
      console.log(`⚠️ SinglePathAnimationEngine: Scene route → SceneAdapter.showSafeZoneWarning(${center.x}, ${center.y}, ${fromRadius}, ${toRadius})`);
      this.sceneAdapter?.showSafeZoneWarning?.(center.x, center.y, fromRadius, toRadius);
      this.finish(agentId, seq);
      return;
    }
    if (type === EventTypes.SAFEZONE_CONTRACTION_STARTED) {
      const contraction = data.contraction || {};
      const from = contraction.from || { center: { x: 0, y: 0 }, radius: 100 };
      const to = contraction.to || { center: { x: 0, y: 0 }, radius: 50 };
      const duration = contraction.estimatedDuration || 3000;
      console.log(`🔄 SinglePathAnimationEngine: Scene route → SceneAdapter.animateSafeZoneContraction(${from.center.x}, ${from.center.y}, ${from.radius}, ${to.radius}, ${duration})`);
      this.sceneAdapter?.animateSafeZoneContraction?.(from.center.x, from.center.y, from.radius, to.radius, duration);
      this.finish(agentId, seq);
      return;
    }
    if (type === EventTypes.SAFEZONE_CONTRACTION_COMPLETED) {
      const safeZone = data.safeZone || {};
      const center = safeZone.center || { x: 0, y: 0 };
      const radius = safeZone.radius || 50;
      console.log(`✅ SinglePathAnimationEngine: Scene route → SceneAdapter.updateSafeZoneDisplay(${center.x}, ${center.y}, ${radius})`);
      this.sceneAdapter?.updateSafeZoneDisplay?.(center.x, center.y, radius);
      this.finish(agentId, seq);
      return;
    }
    if (type === EventTypes.SAFEZONE_UPDATED) {
      const center = data.center || { x: 0, y: 0 };
      const radius = data.currentRadius || 50;
      console.log(`🔄 SinglePathAnimationEngine: Scene route → SceneAdapter.updateSafeZoneDisplay(${center.x}, ${center.y}, ${radius})`);
      this.sceneAdapter?.updateSafeZoneDisplay?.(center.x, center.y, radius);
      this.finish(agentId, seq);
      return;
    }

    if (type === EventTypes.SAFEZONE_DAMAGE_APPLIED || type === EventTypes.ENVIRONMENT_DAMAGE) {
      console.log(`🌪️ SinglePathAnimationEngine: Scene route → SceneAdapter.hurt(${agentId}) for environment damage`);
      this.sceneAdapter?.onComplete(agentId, () => this.finish(agentId, seq));
      this.sceneAdapter?.hurt(agentId);
      return;
    }

    // Combat layer
    if (type === EventTypes.COMBAT_STARTED || type === EventTypes.COMBAT_DAMAGE_DEALT || type === EventTypes.COMBAT_ATTACK_MISSED || type === EventTypes.COMBAT_ENDED) {
      // 🎯 v3.2.8: 记录战斗参与者的最近战斗seq，用于后续死亡时序判断
      if (seq !== undefined) {
        const participants = this.getCombatParticipants(data);
        participants.forEach(id => {
          this.recentCombatSeq.set(id, seq);
          console.log(`[Engine-Combat-Track] ${id} recent combat seq updated to ${seq}`);
        });
      }
      // 战斗完全由 CombatEventRouter → Sequencer 管理；Engine 不再处理
      this.finish(agentId, seq);
      return;
    }
  }

  // 🎯 v3.2.8: 从战斗事件数据中提取所有参与者ID
  private getCombatParticipants(data: any): string[] {
    const participants: string[] = [];
    if (data.attackerId) participants.push(data.attackerId);
    if (data.defenderId) participants.push(data.defenderId);
    // 如果有agentId且不在上述列表中，也加入（处理特殊情况）
    if (data.agentId && !participants.includes(data.agentId)) {
      participants.push(data.agentId);
    }
    return participants;
  }

  private isPathReady(type: string): boolean {
    const sceneEvents = new Set<string>([
      EventTypes.AGENT_SPAWNED,
      EventTypes.AGENT_MOVED,
      EventTypes.AGENT_ELIMINATED,
      EventTypes.RESOURCE_COLLECTED,
      EventTypes.WEAPON_EQUIPPED,
      EventTypes.ITEM_USED,
      EventTypes.SAFEZONE_DAMAGE_APPLIED,
      EventTypes.ENVIRONMENT_DAMAGE,
      // 🔥 SafeZone事件属于Scene层
      EventTypes.SAFEZONE_WARNING,
      EventTypes.SAFEZONE_CONTRACTION_STARTED,
      EventTypes.SAFEZONE_CONTRACTION_COMPLETED,
      EventTypes.SAFEZONE_UPDATED
    ]);
    if (sceneEvents.has(type)) return this.ready.scene;
    const theaterEvents = new Set<string>([
      EventTypes.COMBAT_STARTED,
      EventTypes.COMBAT_DAMAGE_DEALT,
      EventTypes.COMBAT_ATTACK_MISSED,
      EventTypes.COMBAT_ENDED
    ]);
    if (theaterEvents.has(type)) return this.ready.theater;
    return true;
  }

  private priorityOf(type: string): number {
    // 🔧 优先级恢复v3.0.146配置，确保MOVED不被ELIMINATED打断
    if (type === EventTypes.AGENT_SPAWNED) return 1;
    if (type === EventTypes.AGENT_ELIMINATED) return 1;
    if (type === EventTypes.COMBAT_STARTED) return 1; // 先建立剧场
    if (type === EventTypes.COMBAT_DAMAGE_DEALT) return 2;
    if (type === EventTypes.AGENT_MOVED) return 2; // 🔧 移动与战斗同级，确保顺序处理
    if (type === EventTypes.SAFEZONE_WARNING || type === EventTypes.SAFEZONE_CONTRACTION_STARTED) return 2; // 🔥 SafeZone高优先级
    if (type === EventTypes.COMBAT_ATTACK_MISSED) return 3;
    if (type === EventTypes.SAFEZONE_CONTRACTION_COMPLETED) return 3; // 🔥 SafeZone视觉效果
    if (type === EventTypes.WEAPON_EQUIPPED || type === EventTypes.ITEM_USED || type === EventTypes.RESOURCE_COLLECTED) return 3;
    if (type === EventTypes.COMBAT_ENDED) return 4;
    if (type === EventTypes.SAFEZONE_UPDATED) return 4;
    return 5;
  }

  private enqueue(agentId: string, ev: GameEvent) {
    if (!this.queues.has(agentId)) this.queues.set(agentId, []);
    const q = this.queues.get(agentId)!;
    const pr = this.priorityOf(ev.type);
    // 淘汰事件：强制排到队尾，禁止插入到其它事件前面
    if (ev.type === EventTypes.AGENT_ELIMINATED) {
      q.push(ev);
      return;
    }
    // 入队去重：对非移动类的同类型事件保留最后一个
    const dedupTypes = new Set<string>([
      EventTypes.WEAPON_EQUIPPED,
      EventTypes.RESOURCE_COLLECTED,
      EventTypes.ITEM_USED
    ]);
    if (dedupTypes.has(ev.type)) {
      for (let i = q.length - 1; i >= 0; i--) {
        if (q[i].type === ev.type) { q.splice(i, 1); }
      }
    }
    let i = 0; while (i < q.length && this.priorityOf(q[i].type) <= pr) i++;
    q.splice(i, 0, ev);
  }

  private finish(agentId: string, seq?: number, immediate: boolean = false) {
    this.currentPriority.set(agentId, Number.MAX_SAFE_INTEGER);
    if (typeof seq === 'number') {
      const last = this.lastSeq.get(agentId) ?? -1;
      if (seq > last) this.lastSeq.set(agentId, seq);
    }

    // 🛰️ 广播单步完成事件（供 PlaybackScheduler 等上层等待动画真实完成）
    try {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ABR_ENGINE_STEP_DONE', {
          detail: { agentId, seq, timestamp: Date.now() }
        }));
      }
    } catch {}

    // 🔥 关键修复：移动完成立即处理队列，避免战斗barrier已释放但移动未处理
    const delay = immediate ? 0 : this.stepInterval;
    setTimeout(() => {
      const q = this.queues.get(agentId);
      if (q && q.length > 0) {
        const next = q.shift()!;
        console.log(`⏰ Engine: ${immediate?'立即':'延迟'+this.stepInterval+'ms'}处理队列中下一个事件 ${next.type} for ${agentId}`);
        this.handle(next);
        if (q.length === 0) this.queues.delete(agentId);
      }
    }, delay);
  }

  private notifyAllMovementsComplete(agentId: string) {
    console.log(`✅🎯 Engine: ${agentId} ALL movements complete, emitting ABR_ALL_MOVES_DONE`);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('ABR_ALL_MOVES_DONE', {
        detail: { agentId, timestamp: Date.now() }
      }));
    }
  }
}

export default SinglePathAnimationEngine;
